import * as React from 'react';
import { AutoSizer } from 'react-virtualized';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IComparisonResult } from '../../../data/providers/reports/comparisonReportProvider';
import { ISize } from '../../../data/types';
import { roundValue } from '../../../utils/math';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { Card } from '../../shared/card/Card';
import { CardBody } from '../../shared/card/CardBody';
import { GraphContainer } from '../../shared/graphs/GraphContainer';
import { legendFormatter } from '../../shared/graphs/legendFormatter';
import { Notable } from '../../shared/pdf/Notable';
import { EPdfMode, isPdf, shouldInvert } from '../../shared/pdf/Pdf';
import { abbreviateMetricLabel } from '../utils/abbreviateMetricLabel';
import { ComparisonTooltip } from './ComparisonTooltip';

interface IProps {
  results: IComparisonResult[];
  pdfMode: EPdfMode;
  colors: string[];
}

interface IDataPoint {
  Metric: string;
  MetricFullLabel: string;
  ['Data Set A']: number;
  ['Data Set B']: number;
}

export const ComparisonGraph = (props: IProps): React.ReactElement => {
  const data = React.useMemo((): IDataPoint[] => {
    return props.results.map((result: IComparisonResult): IDataPoint => {
      return {
        Metric: abbreviateMetricLabel(result.metric.label),
        MetricFullLabel: result.metric.label,
        ['Data Set A']: result.dataSetA.percentage,
        ['Data Set B']: result.dataSetB.percentage,
      };
    });
  }, [JSON.stringify(props.results)]);

  return (
    <Row>
      <Notable pdf={isPdf(props.pdfMode)}>
        <Card
          noTopRadius
          inverse={shouldInvert(props.pdfMode)}
          style={{
            padding: '0px',
            minHeight: 'var(--graph-min-height)',
            ...(isPdf(props.pdfMode) ? { overflow: 'visible', height: 'auto' } : {}),
          }}
        >
          <Column>
            <CardBody withoutFooter notScrollable={isPdf(props.pdfMode)}>
              <GraphContainer
                inverse={shouldInvert(props.pdfMode)}
                style={isPdf(props.pdfMode) ? { height: 'auto' } : undefined}
              >
                <div
                  style={{
                    height: '100%',
                    width: '100%',
                  }}
                  className='graph-parent'
                >
                  <AutoSizer>
                    {(size: ISize): React.ReactElement => {
                      return (
                        <BarChart
                          height={size.height}
                          width={size.width}
                          data={data}
                          margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                        >
                          <CartesianGrid
                            stroke='#ccc'
                            strokeWidth={0.25}
                            vertical={false}
                          />
                          <XAxis
                            dataKey='Metric'
                            axisLine={false}
                            interval={0}
                            tickLine={false}
                            tickCount={6}
                            tick={{
                              fill: 'var(--color-text)',
                              fontSize: '.7em',
                            }}
                          />
                          <YAxis
                            domain={[0, 100]}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: 'var(--color-text)' }}
                          />
                          <Tooltip
                            cursor={false}
                            content={ComparisonTooltip}
                            formatter={(value: number): number =>
                              roundValue(value, 0)
                            }
                          />
                          <Legend
                            iconType='circle'
                            formatter={(value) =>
                              legendFormatter(value, props.pdfMode)
                            }
                          />
                          <Bar
                            key={'1_0'}
                            dataKey='Data Set A'
                            fill={props.colors[0]}
                          />
                          <Bar
                            key={'2_1'}
                            dataKey='Data Set B'
                            fill={props.colors[1]}
                          />
                        </BarChart>
                      );
                    }}
                  </AutoSizer>
                </div>
              </GraphContainer>
            </CardBody>
          </Column>
        </Card>
      </Notable>
    </Row>
  );
};
